import { KeyRound, Wrench } from "lucide-react";
import { CodePanel } from "@/components/code-panel";
import type { BlockDetail } from "./types";

const ROLE_ORDER = [
  "domain",
  "port",
  "adapter",
  "fake",
  "service",
  "route",
  "schema",
  "test",
  "docs",
];

/** Files, settings and wiring — the three things an install actually consists of. */
export function BlockSpec({ block }: { block: BlockDetail }) {
  const files = [...block.files].sort(
    (a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role),
  );

  return (
    <div className="space-y-10">
      <section className="space-y-3">
        <h2 className="text-sm font-medium tracking-tight text-ink-50">
          Files <span className="font-normal text-ink-400">({files.length})</span>
        </h2>
        <ul className="divide-y divide-ink-700 overflow-hidden rounded-lg border border-ink-700 bg-ink-900">
          {files.map((file) => (
            <li key={file.path} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4">
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <span className="w-16 shrink-0 font-mono text-[0.6875rem] text-ink-400">
                  {file.role}
                </span>
                <code className="truncate text-xs text-ink-50">{file.path}</code>
              </div>
              <p className="flex-1 text-xs leading-relaxed text-ink-400">
                {file.description}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {block.env.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <KeyRound size={15} className="text-ink-400" />
            <h2 className="text-sm font-medium tracking-tight text-ink-50">Settings</h2>
          </div>
          <p className="text-xs text-ink-400">
            Prefixed with your app&apos;s env prefix — <code>MYAPP_</code> in a fresh
            template, whatever <code>make init</code> set otherwise.
          </p>
          <ul className="divide-y divide-ink-700 overflow-hidden rounded-lg border border-ink-700 bg-ink-900">
            {block.env.map((entry) => (
              <li key={entry.name} className="space-y-1 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="text-xs text-ink-50">{entry.name}</code>
                  {entry.required && (
                    <span className="rounded-full border border-ink-700 px-1.5 py-px text-[0.625rem] text-ink-400">
                      required
                    </span>
                  )}
                  {/* Secrets are called out because a leaked client secret is the
                      most expensive mistake this catalog can help avoid. */}
                  {entry.secret && (
                    <span className="rounded-full bg-amber-500/15 px-1.5 py-px text-[0.625rem] text-amber-300">
                      secret
                    </span>
                  )}
                  {entry.default && (
                    <span className="font-mono text-[0.625rem] text-ink-400">
                      default: {entry.default}
                    </span>
                  )}
                </div>
                <p className="text-xs leading-relaxed text-ink-400">{entry.description}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {block.wiring.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Wrench size={15} className="text-ink-400" />
            <h2 className="text-sm font-medium tracking-tight text-ink-50">Wiring</h2>
          </div>
          <p className="text-xs leading-relaxed text-ink-400">
            The part a file copy can&apos;t do. Backend blocks aren&apos;t
            self-contained components — they have to be reached into{" "}
            <code>bootstrap.py</code> and <code>settings.py</code> to come alive.
          </p>
          <div className="space-y-4">
            {block.wiring.map((step, index) => (
              <div key={`${step.target}-${index}`} className="space-y-2">
                <p className="text-xs leading-relaxed text-ink-400">{step.description}</p>
                <CodePanel title={step.target} code={step.code} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
